import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listStudents,
  getClassProgress,
  getClassResources,
  getClassInsights,
  resetStudentPassword,
  resetProject,
  deleteTeacherProject,
  createStudent,
  getStudentDetail,
  type Student,
  type ProgressRow,
  type ClassResource,
  type ClassInsights,
  type ChatMessage,
  type StudentProjectDetail,
} from '../api/teacher';
import { AppLayout } from '../components/AppLayout';
import { EmptyState, LoadingDots } from '../components/ChildFriendly';
import { ConfirmModal } from '../components/ConfirmModal';

const EVIDENCE_META: Record<string, { label: string; icon: string }> = {
  anket: { label: 'Anket / Veri', icon: '📊' },
  foto:  { label: 'Fotoğraf',     icon: '📸' },
  ses:   { label: 'Ses Kaydı',    icon: '🎤' },
  video: { label: 'Video',        icon: '🎥' },
  cizim: { label: 'Çizim / Şema', icon: '✏️' },
  metin: { label: 'Metin / Not',  icon: '📝' },
  yok:   { label: 'Kanıt Yok',    icon: '❓' },
};

type TabType = 'students' | 'projects' | 'chat' | 'resources' | 'insights';

export default function TeacherDashboardPage() {
  const navigate = useNavigate();

  // Active tab state
  const [activeTab, setActiveTab] = useState<TabType>('students');

  // Data states
  const [students, setStudents] = useState<Student[]>([]);
  const [progress, setProgress] = useState<ProgressRow[]>([]);
  const [resources, setResources] = useState<ClassResource[]>([]);
  const [insights, setInsights] = useState<ClassInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search & Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [evidenceFilter, setEvidenceFilter] = useState('all');

  // Chat tab states
  const [selectedStudentForChat, setSelectedStudentForChat] = useState<number | null>(null);
  const [studentProjects, setStudentProjects] = useState<StudentProjectDetail[]>([]);
  const [selectedProjectForChat, setSelectedProjectForChat] = useState<string>('all');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);

  // Password reset modal states
  const [passwordModalStudent, setPasswordModalStudent] = useState<Student | null>(null);
  const [customPassword, setCustomPassword] = useState('');
  const [passwordSuccessMsg, setPasswordSuccessMsg] = useState<string | null>(null);
  const [passwordErrorMsg, setPasswordErrorMsg] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    projectId: number;
    studentName: string;
    problemText: string | null;
  } | null>(null);
  const [deletingProject, setDeletingProject] = useState(false);

  // New Student modal/form states
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStudent, setNewStudent] = useState({ display_name: '', username: '', password: '' });
  const [createdPasswordBanner, setCreatedPasswordBanner] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listStudents(),
      getClassProgress(),
      getClassResources(),
      getClassInsights(),
    ])
      .then(([s, p, r, i]) => {
        if (cancelled) return;
        // API sözleşmesine göre hepsi array/object olmalı, ama bazen
        // backend 401/logout sonrası error response'u 200 ile dönebiliyor
        // ya da axios interceptor HTML döndürebiliyor. Defensive guard.
        if (Array.isArray(s)) setStudents(s); else { console.warn('[teacher] students not array:', s); setStudents([]); }
        if (Array.isArray(p)) setProgress(p); else { console.warn('[teacher] progress not array:', p); setProgress([]); }
        if (Array.isArray(r)) setResources(r); else { console.warn('[teacher] resources not array:', r); setResources([]); }
        setInsights(i ?? null);
        if (Array.isArray(s) && s.length > 0) {
          setSelectedStudentForChat(s[0].id);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Veriler yüklenemedi.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch chat messages when selectedStudentForChat changes
  useEffect(() => {
    if (!selectedStudentForChat) return;
    let cancelled = false;
    setChatLoading(true);
    setSelectedProjectForChat('all');
    getStudentDetail(selectedStudentForChat)
      .then((data) => {
        if (cancelled) return;
        const msgs = Array.isArray(data?.messages) ? data.messages : [];
        const projs = Array.isArray(data?.projects) ? data.projects : [];
        if (!Array.isArray(data?.messages)) console.warn('[teacher] student detail messages not array:', data);
        setChatMessages(msgs);
        setStudentProjects(projs);
      })
      .catch(() => {
        if (!cancelled) {
          setChatMessages([]);
          setStudentProjects([]);
        }
      })
      .finally(() => {
        if (!cancelled) setChatLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedStudentForChat]);

  async function handleCreateStudent(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const res = await createStudent(
        newStudent.display_name,
        newStudent.username,
        newStudent.password || undefined,
      );
      setStudents((prev) => [...prev, res.student]);
      setCreatedPasswordBanner(`Öğrenci oluşturuldu! Giriş şifresi: ${res.temp_password}`);
      setNewStudent({ display_name: '', username: '', password: '' });
      setShowAddModal(false);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Öğrenci oluşturulamadı.');
    }
  }

  async function handleUpdatePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!passwordModalStudent) return;
    setPasswordErrorMsg(null);
    setPasswordSuccessMsg(null);
    try {
      const res = await resetStudentPassword(
        passwordModalStudent.id,
        customPassword.trim() || undefined,
      );
      setPasswordSuccessMsg(`Şifre başarıyla güncellendi: ${res.temp_password}`);
      setCustomPassword('');
      setTimeout(() => {
        setPasswordModalStudent(null);
        setPasswordSuccessMsg(null);
      }, 2000);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setPasswordErrorMsg(detail || 'Şifre güncellenemedi.');
    }
  }

  function handleGenerateRandomPassword() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#';
    let rand = '';
    for (let i = 0; i < 10; i++) {
      rand += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCustomPassword(rand);
  }

  async function handleResetProject(projectId: number) {
    if (!window.confirm('Bu proje ve tüm sohbet geçmişi silinecek. Emin misin?')) return;
    try {
      await resetProject(projectId);
      setProgress((prev) => prev.filter((p) => p.project_id !== projectId));
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Proje sıfırlanamadı.');
    }
  }

  async function confirmDeleteProject() {
    if (!pendingDelete) return;
    setDeletingProject(true);
    try {
      await deleteTeacherProject(pendingDelete.projectId);
      // Listeden tamamen kaldır (silme ≠ sıfırlama)
      setProgress((prev) =>
        prev.filter((p) => p.project_id !== pendingDelete.projectId),
      );
      setPendingDelete(null);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Proje silinemedi.');
    } finally {
      setDeletingProject(false);
    }
  }

  if (loading) {
    return (
      <AppLayout title="Öğretmen Paneli">
        <LoadingDots message="Öğretmen paneli yükleniyor" />
      </AppLayout>
    );
  }

  // Calculate high-level stats
  const activeProjectsCount = progress.filter((p) => (p.status === 'in_progress' || p.status === 'pending') && p.project_id !== null).length;
  const completedProjectsCount = progress.filter((p) => p.status === 'passed').length;
  const avgScores = progress
    .map((p) => (p.average_score ? parseFloat(p.average_score) : null))
    .filter((v): v is number => v !== null);
  const classAvgScore = avgScores.length > 0
    ? (avgScores.reduce((a, b) => a + b, 0) / avgScores.length).toFixed(1)
    : '—';

  // Filtered lists
  const filteredStudents = students.filter((s) =>
    s.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.username.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredProgress = progress.filter((p) => {
    const matchesSearch = p.student_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.problem_text && p.problem_text.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredResources = resources.filter((r) => {
    const matchesSearch = r.student_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.role_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = evidenceFilter === 'all' || r.evidence_type === evidenceFilter;
    return matchesSearch && matchesType;
  });

  const filteredChatMessages = chatMessages.filter((m) => {
    if (selectedProjectForChat === 'all') return true;
    return m.project_id === Number(selectedProjectForChat);
  });

  return (
    <AppLayout
      title="Öğretmen Paneli"
      subtitle="Sınıfını yönet: öğrencilerin tüm projelerini, YZ konuşmalarını, yükledikleri kaynakları ve şifrelerini kontrol et."
      width="wide"
    >
      {error && (
        <div role="alert" className="error" style={{ marginBottom: 16 }}>
          <span aria-hidden="true">⚠️</span> {error}
        </div>
      )}

      {createdPasswordBanner && (
        <div role="status" className="temp-password-banner" style={{ marginBottom: 16 }}>
          <span>{createdPasswordBanner}</span>
          <button type="button" onClick={() => setCreatedPasswordBanner(null)}>Kapat</button>
        </div>
      )}

      {/* Hero Stats Section */}
      <section className="teacher-hero-stats" aria-label="Sınıf Özet İstatistikleri">
        <div className="stat-card">
          <div className="stat-card__icon">👥</div>
          <div>
            <div className="stat-card__val">{students.length}</div>
            <div className="stat-card__label">Kayıtlı Öğrenci</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon">🚀</div>
          <div>
            <div className="stat-card__val">{activeProjectsCount}</div>
            <div className="stat-card__label">Devam Eden Proje</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon">🎓</div>
          <div>
            <div className="stat-card__val">{completedProjectsCount}</div>
            <div className="stat-card__label">Tamamlanan (Mezun)</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon">⭐</div>
          <div>
            <div className="stat-card__val">{classAvgScore}</div>
            <div className="stat-card__label">Sınıf Puan Ortalaması</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon">📂</div>
          <div>
            <div className="stat-card__val">{resources.length}</div>
            <div className="stat-card__label">Yüklenen Kaynak</div>
          </div>
        </div>
      </section>

      {/* Main Tab Navigation */}
      <nav className="teacher-nav-tabs" aria-label="Panel Sekmeleri">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'students' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          👥 Öğrenciler ({students.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'projects' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          🚀 Projeler & İlerleme ({progress.filter(p => p.project_id !== null).length})
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'chat' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          💬 YZ Konuşmaları
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'resources' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('resources')}
        >
          📂 Kaynaklar & Medya ({resources.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'insights' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          📊 Sınıf Analitiği
        </button>
      </nav>

      {/* TAB 1: ÖĞRENCİLER */}
      {activeTab === 'students' && (
        <section className="stack">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <input
              type="text"
              className="teacher-search-input"
              placeholder="Öğrenci adı veya kullanıcı adı ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              type="button"
              className="btn btn--primary btn--md"
              onClick={() => setShowAddModal(true)}
            >
              + Yeni Öğrenci Ekle
            </button>
          </div>

          {filteredStudents.length === 0 ? (
            <EmptyState
              icon="👥"
              title="Öğrenci bulunamadı"
              message="Arama kriterlerine uygun öğrenci yok veya henüz öğrenci eklenmemiş."
            />
          ) : (
            <ul className="student-list">
              {filteredStudents.map((s) => {
                const initials = (s.display_name || s.username)
                  .split(/\s+/)[0]?.slice(0, 2).toUpperCase() ?? '?';
                return (
                  <li key={s.id} className="student-list__item">
                    <span className="student-list__avatar" aria-hidden="true">
                      {initials}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div className="student-list__name">{s.display_name}</div>
                      <div className="student-list__sub">
                        @{s.username} · {s.classroom_name}
                      </div>
                    </div>
                    <div className="student-list__actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => navigate(`/teacher/students/${s.id}`)}
                      >
                        📊 Detay & Sohbetler
                      </button>
                      <button
                        type="button"
                        className="btn btn--primary btn--sm"
                        onClick={() => {
                          setPasswordModalStudent(s);
                          setCustomPassword('');
                          setPasswordSuccessMsg(null);
                          setPasswordErrorMsg(null);
                        }}
                      >
                        🔑 Şifre Değiştir
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* TAB 2: PROJELER VE İLERLEME */}
      {activeTab === 'projects' && (
        <section className="stack">
          <div className="teacher-filter-bar">
            <input
              type="text"
              className="teacher-search-input"
              placeholder="Öğrenci adı veya proje problemi ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <select
              className="form-input"
              style={{ width: 'auto' }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tüm Durumlar</option>
              <option value="in_progress">Devam Ediyor</option>
              <option value="pending">Bekliyor</option>
              <option value="passed">Onaylandı (Geçti)</option>
              <option value="failed">Reddedildi</option>
              <option value="draft">Taslak</option>
              <option value="no_project">Projesiz</option>
            </select>
          </div>

          {filteredProgress.length === 0 ? (
            <EmptyState
              icon="🚀"
              title="Proje verisi yok"
              message="Filtreleme kriterlerine uygun proje bulunamadı."
            />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Öğrenci</th>
                    <th>Proje Problemi</th>
                    <th>Aktif Rol / Aşama</th>
                    <th>Ortalama Skor</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProgress.map((p, idx) => (
                    <tr key={p.project_id ? `proj-${p.project_id}` : `stu-${p.student_id}-${idx}`}>
                      <td>
                        <div className="data-table__name">{p.student_name}</div>
                      </td>
                      <td style={{ maxWidth: 300 }}>
                        {p.problem_text ? (
                          <strong title={p.problem_text} style={{ color: '#1e293b' }}>{p.problem_text}</strong>
                        ) : (
                          <span className="text-muted">— (Henüz Proje Açılmadı)</span>
                        )}
                      </td>
                      <td>
                        {p.current_role ? (
                          <span>{p.current_role}</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                        {p.total_roles > 0 && (
                          <div className="data-table__sub" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                            <span style={{ fontWeight: 600, color: '#1e293b' }}>
                              {p.passed_count} / {p.total_roles}
                            </span>
                            <span style={{ color: '#64748b', fontSize: '0.85em' }}>rol geçildi</span>
                            <span
                              role="progressbar"
                              aria-valuenow={p.passed_count}
                              aria-valuemin={0}
                              aria-valuemax={p.total_roles}
                              aria-label={`${p.passed_count} / ${p.total_roles} rol geçildi`}
                              style={{
                                display: 'inline-block',
                                width: 60,
                                height: 6,
                                background: '#e2e8f0',
                                borderRadius: 3,
                                overflow: 'hidden',
                                flexShrink: 0,
                              }}
                            >
                              <span
                                style={{
                                  display: 'block',
                                  width: `${Math.min(100, (p.passed_count / p.total_roles) * 100)}%`,
                                  height: '100%',
                                  background:
                                    p.passed_count === p.total_roles
                                      ? 'linear-gradient(90deg, #10b981, #059669)'
                                      : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                                  transition: 'width 0.3s',
                                }}
                              />
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        {p.average_score !== null && p.average_score !== undefined ? (
                          <span className="badge badge--score" style={{ fontWeight: 700, padding: '4px 8px', borderRadius: 6, background: '#e0e7ff', color: '#3730a3' }}>
                            ⭐ {p.average_score}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <span className={`status-badge status-${p.status}`}>
                          {statusLabel(p.status)}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {p.project_id !== null && (
                            <button
                              type="button"
                              className="btn btn--primary btn--sm"
                              onClick={() => navigate(`/projects/${p.project_id}`)}
                              title="Öğrenci arayüzü görünümüyle bu projeyi aç"
                            >
                              🌐 Arayüzü İncele
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            style={{ fontWeight: 600, color: '#4f46e5' }}
                            onClick={() => navigate(`/teacher/students/${p.student_id}`)}
                          >
                            💬 Detay & Sohbetler
                          </button>
                          {p.project_id !== null && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              style={{ color: '#dc2626' }}
                              onClick={() => handleResetProject(p.project_id!)}
                            >
                              Sıfırla
                            </button>
                          )}
                          {p.project_id !== null && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              style={{ color: '#dc2626', fontWeight: 700 }}
                              onClick={() =>
                                setPendingDelete({
                                  projectId: p.project_id!,
                                  studentName: p.student_name,
                                  problemText: p.problem_text,
                                })
                              }
                            >
                              🗑 Sil
                            </button>
                          )}
                        </div>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* TAB 3: YZ KONUŞMALARI */}
      {activeTab === 'chat' && (
        <section className="stack">
          <div className="card stack" style={{ background: '#f8fafc', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
              <div>
                <label htmlFor="student-chat-select" className="form-label" style={{ fontWeight: 700 }}>
                  1. Öğrenci Seçin:
                </label>
                <select
                  id="student-chat-select"
                  className="form-input"
                  value={selectedStudentForChat ?? ''}
                  onChange={(e) => setSelectedStudentForChat(Number(e.target.value))}
                >
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.display_name} (@{s.username})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="project-chat-select" className="form-label" style={{ fontWeight: 700 }}>
                  2. İncelenecek Projeyi Seçin:
                </label>
                <select
                  id="project-chat-select"
                  className="form-input"
                  value={selectedProjectForChat}
                  onChange={(e) => setSelectedProjectForChat(e.target.value)}
                >
                  <option value="all">Tüm Projeler (Toplam {chatMessages.length} Mesaj)</option>
                  {studentProjects.map((proj) => (
                    <option key={proj.id} value={proj.id}>
                      "{proj.problem_text.slice(0, 45)}..." ({statusLabel(proj.status)})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {chatLoading ? (
            <LoadingDots message="Öğrencinin YZ diyalogları getiriliyor" />
          ) : filteredChatMessages.length === 0 ? (
            <EmptyState
              icon="💬"
              title="Konuşma bulunamadı"
              message="Seçilen projede veya öğrencide henüz YZ mesajı bulunmuyor."
            />
          ) : (
            <div className="chat-thread" style={{ marginTop: '1rem' }}>
              {filteredChatMessages.map((m) => {
                const evidence = m.evidence_type ? EVIDENCE_META[m.evidence_type] : null;
                return (
                  <article
                    key={m.id}
                    className={`chat-msg is-${m.sender}`}
                    style={{ marginBottom: '1rem' }}
                  >
                    <div className="chat-msg__meta">
                      <span className="chat-msg__role">
                        {m.sender === 'student' ? '🧒 Öğrenci' : '🤖 YZ Kişisi'}
                      </span>
                      <span>·</span>
                      <strong>{m.role_name}</strong>
                      <span>·</span>
                      <time>{new Date(m.created_at).toLocaleString('tr-TR')}</time>
                      {evidence && m.sender === 'student' && (
                        <>
                          <span>·</span>
                          <span className="badge" style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                            {evidence.icon} {evidence.label}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="chat-msg__content" style={{ whiteSpace: 'pre-wrap' }}>{m.content}</p>
                    {m.attached_file && (
                      <div className="chat-msg__attachment" style={{ marginTop: '0.5rem' }}>
                        📎 <a href={m.attached_file} target="_blank" rel="noreferrer">Yüklenen Ek Dosyayı Görüntüle ↗</a>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* TAB 4: KAYNAKLAR VE MEDYA VAULT */}
      {activeTab === 'resources' && (
        <section className="stack">
          <div className="teacher-filter-bar">
            <input
              type="text"
              className="teacher-search-input"
              placeholder="Öğrenci veya rol ismi ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <select
              className="form-input"
              style={{ width: 'auto' }}
              value={evidenceFilter}
              onChange={(e) => setEvidenceFilter(e.target.value)}
            >
              <option value="all">Tüm Kanıt Türleri</option>
              <option value="foto">📸 Fotoğraf</option>
              <option value="ses">🎤 Ses Kaydı</option>
              <option value="video">🎥 Video</option>
              <option value="cizim">✏️ Çizim / Şema</option>
              <option value="anket">📊 Anket / Veri</option>
              <option value="metin">📝 Metin / Belge</option>
            </select>
          </div>

          {filteredResources.length === 0 ? (
            <EmptyState
              icon="📂"
              title="Yüklenmiş kaynak yok"
              message="Öğrenciler henüz dosya veya medya yüklemedi."
            />
          ) : (
            <div className="resource-grid">
              {filteredResources.map((r) => {
                const meta = EVIDENCE_META[r.evidence_type] || { label: r.evidence_type, icon: '📄' };
                const isImage = r.attached_file.match(/\.(jpeg|jpg|gif|png|webp)$/i);
                const isAudio = r.attached_file.match(/\.(mp3|wav|ogg|m4a)$/i);
                const isVideo = r.attached_file.match(/\.(mp4|webm|mov)$/i);

                return (
                  <div key={r.id} className="resource-card">
                    <div className="resource-card__header">
                      <div>
                        <div className="resource-card__student">{r.student_name}</div>
                        <div className="resource-card__role">{r.role_name}</div>
                      </div>
                      <span title={meta.label} style={{ fontSize: '1.4rem' }}>{meta.icon}</span>
                    </div>

                    <div className="resource-card__preview">
                      {isImage ? (
                        <img src={r.attached_file} alt="Öğrenci Yüklemesi" />
                      ) : isAudio ? (
                        <audio controls src={r.attached_file} />
                      ) : isVideo ? (
                        <video controls src={r.attached_file} />
                      ) : (
                        <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#6b7280' }}>
                          📄 {meta.label} Belgesi
                        </div>
                      )}
                    </div>

                    <p style={{ fontSize: '0.85rem', color: '#4b5563', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      "{r.content}"
                    </p>

                    <div className="resource-card__footer">
                      <time style={{ color: '#9ca3af' }}>{new Date(r.created_at).toLocaleDateString('tr-TR')}</time>
                      <a href={r.attached_file} target="_blank" rel="noreferrer" className="btn btn--ghost btn--sm">
                        Aç / İndir ↗
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* TAB 5: SINIF ANALİTİĞİ */}
      {activeTab === 'insights' && insights && (
        <section className="stack">
          <div className="insights-grid">
            <div className="insight-card">
              <h4>🎯 Başarı Oranları</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>İlk Denemede Geçme Oranı:</span>
                  <strong>%{Math.round(insights.first_attempt_pass_rate * 100)}</strong>
                </li>
                <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Öğrenci Başına Ort. Kanıt:</span>
                  <strong>{insights.average_evidence_per_student.toFixed(1)} adet</strong>
                </li>
                <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>En Çok Zorlanılan Rol:</span>
                  <strong style={{ color: '#dc2626' }}>{insights.hardest_role || 'Yok'}</strong>
                </li>
              </ul>
            </div>

            <div className="insight-card">
              <h4>🔥 Popüler Proje Konuları</h4>
              {Object.keys(insights.problem_popularity).length === 0 ? (
                <p className="text-muted">Henüz proje verisi yok.</p>
              ) : (
                <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
                  {Object.entries(insights.problem_popularity).map(([prob, count]) => (
                    <li key={prob} style={{ marginBottom: '0.4rem' }}>
                      "{prob}..." — <strong>{count} proje</strong>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="insight-card">
              <h4>⚠️ Reddedilme Nedenlerindeki Temalar</h4>
              {Object.keys(insights.common_themes_in_failures).length === 0 ? (
                <p className="text-muted">Henüz başarısızlık teması tespit edilmedi.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {Object.entries(insights.common_themes_in_failures).map(([theme, count]) => (
                    <span key={theme} className="badge" style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 10px', borderRadius: 16 }}>
                      {theme} ({count})
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* MODAL: ÖZEL ŞİFRE GÜNCELLEME */}
      {passwordModalStudent && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="pwd-modal-title">
          <div className="modal-content">
            <div className="modal-header">
              <h3 id="pwd-modal-title">🔑 Şifre Güncelle</h3>
              <button type="button" className="modal-close" onClick={() => setPasswordModalStudent(null)}>×</button>
            </div>
            <p style={{ fontSize: '0.95rem', color: '#4b5563', marginBottom: '1rem' }}>
              <strong>{passwordModalStudent.display_name}</strong> (@{passwordModalStudent.username}) için yeni bir şifre belirleyin.
            </p>

            {passwordSuccessMsg && (
              <div role="status" style={{ background: '#dcfce7', color: '#166534', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem' }}>
                ✅ {passwordSuccessMsg}
              </div>
            )}
            {passwordErrorMsg && (
              <div role="alert" className="error" style={{ marginBottom: '1rem' }}>
                ⚠️ {passwordErrorMsg}
              </div>
            )}

            <form onSubmit={handleUpdatePassword} className="stack">
              <div className="form-field">
                <label htmlFor="custom_pwd" className="form-label">Özel Şifre Belirleyin</label>
                <div className="password-input-group">
                  <input
                    id="custom_pwd"
                    type="text"
                    className="form-input"
                    placeholder="Örn. Ogrenci2026!"
                    value={customPassword}
                    onChange={(e) => setCustomPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={handleGenerateRandomPassword}
                    title="Rastgele Şifre Oluştur"
                  >
                    🎲 Rastgele
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
                <button type="button" className="btn btn--ghost btn--md" onClick={() => setPasswordModalStudent(null)}>İptal</button>
                <button type="submit" className="btn btn--primary btn--md">Şifreyi Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: YENİ ÖĞRENCİ EKLE */}
      {showAddModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="add-modal-title">
          <div className="modal-content">
            <div className="modal-header">
              <h3 id="add-modal-title">➕ Yeni Öğrenci Ekle</h3>
              <button type="button" className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateStudent} className="stack" noValidate>
              <div className="form-field">
                <label htmlFor="modal_display_name" className="form-label">Görünür Ad (Ad Soyad)</label>
                <input
                  id="modal_display_name"
                  type="text"
                  className="form-input"
                  placeholder="Örn. Ayşe Yılmaz"
                  value={newStudent.display_name}
                  onChange={(e) => setNewStudent((s) => ({ ...s, display_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="modal_username" className="form-label">Kullanıcı Adı</label>
                <input
                  id="modal_username"
                  type="text"
                  className="form-input"
                  placeholder="Örn. ayse123"
                  value={newStudent.username}
                  onChange={(e) => setNewStudent((s) => ({ ...s, username: e.target.value }))}
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="modal_password" className="form-label">Şifre (İsteğe Bağlı)</label>
                <input
                  id="modal_password"
                  type="text"
                  className="form-input"
                  placeholder="Boş bırakılırsa otomatik üretilir"
                  value={newStudent.password}
                  onChange={(e) => setNewStudent((s) => ({ ...s, password: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
                <button type="button" className="btn btn--ghost btn--md" onClick={() => setShowAddModal(false)}>İptal</button>
                <button type="submit" className="btn btn--primary btn--md">Öğrenciyi Kaydet</button>
              </div>
</form>
         </div>
       </div>
     )}

     <ConfirmModal
       open={pendingDelete !== null}
       title="Öğrenci projesini kalıcı olarak sil"
       message={`${pendingDelete?.studentName ?? ''} öğrencisinin projesi ve TÜM sohbet geçmişi, kanıt dosyaları ve diploması silinecek.`}
       highlight={
         pendingDelete?.problemText
           ? `📝 ${pendingDelete.problemText.slice(0, 80)}${pendingDelete.problemText.length > 80 ? '…' : ''}`
           : undefined
       }
       warning="Bu işlem geri alınamaz!"
       confirmLabel="Evet, kalıcı olarak sil"
       cancelLabel="Vazgeç"
       tone="danger"
       loading={deletingProject}
       onConfirm={confirmDeleteProject}
       onCancel={() => !deletingProject && setPendingDelete(null)}
     />
   </AppLayout>
 );
}

function statusLabel(status: string): string {
  switch (status) {
    case 'draft': return 'Taslak';
    case 'in_progress': return 'Devam ediyor';
    case 'pending': return 'Bekliyor';
    case 'passed': return 'Onaylandı (Geçti)';
    case 'failed': return 'Reddedildi';
    case 'no_project': return 'Proje yok';
    default: return status;
  }
}
